import { FabricImage, IText } from "fabric";
import { getCanvas } from "./CanvasManager";
import { useDesignerStore } from "../store";
import { alignmentManager } from "./AlignmentManager";

// Large, easy-to-grab handles so artwork is simple to move and resize (incl. touch).
const HANDLE_STYLE = {
  cornerStyle: "circle" as const,
  transparentCorners: false,
  cornerColor: "#0ea5e9",
  cornerStrokeColor: "#ffffff",
  borderColor: "#0ea5e9",
  cornerSize: 16,
  touchCornerSize: 28,
  padding: 8,
};

class ObjectManager {

  private updateSelectedSize(object: any) {
    const width = object.getScaledWidth() / 10;
    const height = object.getScaledHeight() / 10;

    useDesignerStore.getState().setSelectedSize(
      Number(width.toFixed(1)),
      Number(height.toFixed(1))
    );
  }

  private setSelectedObject(object: any | null) {
    useDesignerStore.getState().setSelectedObject(object);
  }


  private attachEvents(object: any) {

    object.on("selected", () => {
      this.setSelectedObject(object);
      this.updateSelectedSize(object);
    });

    object.on("moving", () => {
      // No print-area clamping — artwork moves freely anywhere on the artboard.
      alignmentManager.snap(object);
      alignmentManager.showCenterGuides();
      this.updateSelectedSize(object);
    });

    object.on("scaling", () => {
      this.updateSelectedSize(object);
    });

    object.on("modified", () => {
      alignmentManager.hideGuides();
      this.updateSelectedSize(object);
    });

    object.on("mouseup", () => {
      alignmentManager.hideGuides();
      this.updateSelectedSize(object);
    });

    object.on("deselected", () => {
      this.setSelectedObject(null);
      useDesignerStore.getState().setSelectedSize(0, 0);
    });
  }

  async addLogo(imageUrl: string) {

    const canvas = getCanvas();

    if (!canvas) return;

    const logo = await FabricImage.fromURL(imageUrl);

    logo.set({
      left: canvas.width! / 2,
      top: canvas.height! / 2,
      originX: "center",
      originY: "center",
      ...HANDLE_STYLE,
    });

    logo.scaleToWidth(180);

    this.attachEvents(logo);

    canvas.add(logo);
    canvas.setActiveObject(logo);

    this.setSelectedObject(logo);

    useDesignerStore.getState().addObject({
      id: crypto.randomUUID(),
      name: "Logo",
      type: "image",
      fabricObject: logo,
      visible: true,
      locked: false,
    });

    this.updateSelectedSize(logo);

    canvas.renderAll();
  }

  addText(text: string) {

    const canvas = getCanvas();

    if (!canvas) return;

    const object = new IText(text, {
      left: canvas.width! / 2,
      top: canvas.height! / 2,
      originX: "center",
      originY: "center",

      fontSize: 42,
      fill: "#000000",
      fontFamily: "Arial",
      ...HANDLE_STYLE,
    });

    this.attachEvents(object);

    canvas.add(object);
    canvas.setActiveObject(object);

    this.setSelectedObject(object);

    useDesignerStore.getState().addObject({
      id: crypto.randomUUID(),
      name: text,
      type: "text",
      fabricObject: object,
      visible: true,
      locked: false,
    });

    this.updateSelectedSize(object);

    canvas.renderAll();
  }

  deleteSelected() {

    const canvas = getCanvas();

    if (!canvas) return;

    const active = canvas.getActiveObject();

    if (!active) return;

    const objects = useDesignerStore.getState().objects;

    const layer = objects.find(
      (o) => o.fabricObject === active
    );

    if (layer) {
      useDesignerStore.getState().removeObject(layer.id);
    }

    canvas.remove(active);

    this.setSelectedObject(null);

    useDesignerStore.getState().setSelectedSize(0, 0);

    alignmentManager.hideGuides();

    canvas.renderAll();
  }

  duplicateSelected() {

    const canvas = getCanvas();

    if (!canvas) return;

    const active = canvas.getActiveObject();

    if (!active) return;

    active.clone().then((clone: any) => {

      clone.set({
        left: (active.left ?? 0) + 25,
        top: (active.top ?? 0) + 25,
      });

      this.attachEvents(clone);

      canvas.add(clone);
      canvas.setActiveObject(clone);

      this.setSelectedObject(clone);

      useDesignerStore.getState().addObject({
        id: crypto.randomUUID(),
        name:
          active.type === "i-text"
            ? "Copy of Text"
            : "Copy of Logo",
        type:
          active.type === "i-text"
            ? "text"
            : "image",
        fabricObject: clone,
        visible: true,
        locked: false,
      });

      this.updateSelectedSize(clone);

      canvas.renderAll();

    });

  }

  bringForwardSelected() {

    const canvas = getCanvas();

    if (!canvas) return;

    const active = canvas.getActiveObject();

    if (!active) return;

    canvas.bringObjectForward(active);

    canvas.renderAll();

  }

  sendBackwardSelected() {

    const canvas = getCanvas();

    if (!canvas) return;

    const active = canvas.getActiveObject();

    if (!active) return;

    canvas.sendObjectBackwards(active);

    canvas.renderAll();

  }

}

export const objectManager = new ObjectManager();